// Copyright ©️ 2026 eVolve MEP, LLC

import type {
  Facility,
  Project,
  WorkOrder,
  RenderableUser,
  WorkCell,
  TimeFrameData,
  EvolveApiParams,
  StrongExtract,
  EvolveApiRoute,
  TimeFrameOption,
  FacilityId,
  ProjectId,
  WorkOrderStateName,
  StrongExclude,
} from '@evolvemep/api-ts';

import type { ActiveAllFilter } from 'components/Mantine/GlobalFilters/ActiveAllSelect';
import type { EntityState } from 'components/Mantine/StatusEditor/types';
import type { TimeFrameGroup } from 'components/Mantine/TimeFramePicker/TimeFramePicker';

export type GlobalFilterValues<ActiveAll = unknown> = {
  facilities: Facility[];
  projects: Project[];
  workOrders: WorkOrder[];
  assignees: RenderableUser[];
  workCells: WorkCell[];
  timeFrame: TimeFrameData;
  workOrderStates: EntityState<'Work Order'>[];
  activeAll: {
    key: string;
    state: ActiveAllFilter;
    value: ActiveAll[] | undefined;
  };
};

export const globalFilterDefaults: GlobalFilterValues = {
  facilities: [],
  projects: [],
  workOrders: [],
  assignees: [],
  workCells: [],
  workOrderStates: [],
  timeFrame: {
    timeFrame: null,
    date: null,
    dateRange: null,
  },
  activeAll: {
    key: 'generic',
    state: 'active',
    value: undefined,
  },
};

export type GlobalFiltersConfig<ActiveAll = unknown> = Partial<{
  facilities: boolean;
  projects: boolean;
  workOrders: boolean | EvolveApiParams<'GET_PAGINATED', 'shop/workOrder'>;
  assignees: StrongExtract<
    EvolveApiRoute<'GET'>,
    'shop/task/assignedUsers' | 'shop/workOrder/assignedUsers' | 'shop/shipment/assignedUsers'
  >;
  workCells: boolean;
  workOrderStates: WorkOrderStateName[];
  timeFrame: {
    defaultTimeFrame: StrongExclude<TimeFrameOption, 'Specific Date' | 'Date Range'>;
    options?: TimeFrameOption[];
    /** If provided, will override `options` */
    groups?: TimeFrameGroup[];
  };
  activeAll: {
    key: string;
    defaultState?: ActiveAllFilter;
    whenActive?: ActiveAll[];
    allPossible?: ActiveAll[];
  };
}>;

export type GlobalFilterKey = keyof GlobalFilterValues & keyof GlobalFiltersConfig;

export type GlobalFilterProps<T extends GlobalFilterKey> = {
  filter: GlobalFiltersConfig[T];
  value: GlobalFilterValues[T];
  onChange: (value: GlobalFilterValues[T]) => void;
  defaultParams: {
    facilityIds?: FacilityId[];
    projectIds?: ProjectId[];
  };
};
